// @flow
import { createReducer } from 'redux-act';

import type { User, Scope, Role } from '@gandi/authentication';
import type { Grid } from '@gandi/billing';
import {
  createResource,
  type FetchResponse,
  type FetchError,
  type Resource,
} from '@gandi/resource/redux';

import type { Dispatch, FullState } from '../../../types';

export type ReferResponse = {
  user_id: string,
  created_at: string,
  affiliate: {
    discounts: Array<{
      discount_code: string,
      date_end: string,
      date_start: string,
      id: number,
    }>,
    code: string,
    email: string,
    contracts: Array<mixed>,
    date_delete?: string,
    status: 'active' | 'deleted' | 'expired',
    payment_means: Array<mixed>,
    mail_infos: Array<mixed>,
    status_label: 'active' | 'deleted' | 'expired',
    name: string,
  },
  id: string,
};

// ********** RESOURCE **********

export const referResource: Resource<> = createResource('refer');

// ********** ACTIONS **********

export const fetchAffiliateLink =
  (userId: string): (Dispatch => any) =>
  dispatch => {
    const params = {
      user_id: userId,
      affiliate: 'FRIEND',
    };
    return dispatch(
      referResource.fetch('/affiliate/hashes', {
        params,
      }),
    );
  };

export const postAffiliateLink =
  (userId: string): (Dispatch => any) =>
  dispatch => {
    const params = {
      user_id: userId,
      affiliate: 'FRIEND',
    };
    return dispatch(
      referResource.post('/affiliate/hashes', params, {
        // $FlowFixMe[incompatible-call]
        on20X: (response: FetchResponse<200, true>) => response,
        // $FlowFixMe[incompatible-call]
        not20X: (response: FetchError) => Promise.reject(response),
      }),
    );
  };
// ********** SELECTORS **********
export const selectAffiliateId = (state: FullState): string =>
  state.affiliate.id;

// ********** HANDLERS **********
const fetchHandler = (
  state: User,
  { response }: { response: FetchResponse<200, Array<ReferResponse>>, ... },
): {
  affiliate: {
    code: string,
    contracts: Array<mixed>,
    date_delete?: string,
    discounts: Array<{
      date_end: string,
      date_start: string,
      discount_code: string,
      id: number,
    }>,
    email: string,
    mail_infos: Array<mixed>,
    name: string,
    payment_means: Array<mixed>,
    status: 'active' | 'deleted' | 'expired',
    status_label: 'active' | 'deleted' | 'expired',
  },
  bu_id?: string,
  created_at: string,
  email: string,
  email_validation_deadline: string,
  expires_in: number,
  grid?: Grid,
  id: string,
  lang: string,
  roles: Array<Role>,
  scope: Array<Scope>,
  user_id: string,
  username: string,
  validated_email?: boolean,
  ...
} =>
  // $FlowFixMe[incompatible-return] - expires_in should be fixed in @gandi/authentication
  ({
    ...state,
    ...response.data[0],
  });

// ********** REDUCER **********
const initialState: { ... } = {};

const reducer: {
  (): (state: { ... }, payload?: mixed, meta?: mixed) => { ... },
  assignAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  batch: ({ ... } | Array<mixed>) => mixed,
  bindAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  disbatch: (mixed, void | Array<mixed>) => mixed,
  has: (actionCreator: mixed) => boolean,
  // $FlowFixMe[cannot-resolve-name]
  off: Reducer$Off,
  // $FlowFixMe[cannot-resolve-name]
  on: Reducer$On<{ ... }>,
  options: () => mixed,
  ...
} = createReducer(
  {
    [referResource.fetch.success.getType()]: fetchHandler,
  },
  initialState,
);

export default reducer;
